export type BlogPost = {
  slug: string;
  title: string;
  description: string;
  datePublished: string;
  keywords: string;
  body: string;
};

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'wat-is-koellast',
    title: 'Wat is koellast (en waarom het belangrijk is)',
    description:
      'Koellast verklaard in gewone taal: waarom je ruimte warm wordt, wat je ermee doet in de praktijk, en hoe ComfortTool je helpt inschatten.',
    datePublished: '2026-01-15',
    keywords: 'koellast, koeling, vermogen, comfort, airco, warmtepomp',
    body: `## Waar hebben we het over?

**Koellast** is het vermogen dat je nodig hebt om een ruimte op een warme dag op een prettige temperatuur te houden. Niet alleen de buitentemperatuur telt: zon op ramen, isolatie, ventilatie en wat jij “lekker” vindt bepalen samen hoe zwaar het wordt.

## Waarom het belangrijk is

Te klein gekozen = op hete dagen oncomfortabel. Groter dan nodig = onnodige investering en soms minder fijne regeling. Een **eerste inschatting** helpt je het gesprek met een installateur te voeren — met cijfers die bij jouw situatie passen.

## Hoe ComfortTool helpt

In de **ComfortTool-scan** vul je je ruimtes en gebouw in. Je krijgt indicatieve vermogens en jaarperspectief. Onthoud: dit is **geen** officieel energielabel; het is een praktische manier om grip te krijgen op comfort en vermogen voordat je grote keuzes maakt.

## Volgende stap

**Start de scan** en kijk wat er uitkomt voor jouw situatie. Twijfel je over zonwering of ramen? Probeer een tweede scenario en vergelijk rustig.`,
  },
  {
    slug: 'hoeveel-vermogen-nodig',
    title: 'Hoeveel vermogen heb ik nodig voor mijn ruimte?',
    description:
      'Van m² naar kW: wat vermogen betekent, waarom het per ruimte verschilt, en hoe je met ComfortTool een onderbouwde eerste inschatting krijgt.',
    datePublished: '2026-01-18',
    keywords: 'vermogen kW, verwarming, koeling, ruimte, berekening',
    body: `## Vermogen in het kort

Installaties worden vaak uitgedrukt in **kilowatt (kW)**. Dat is het vermogen op een moment — niet automatisch je jaarverbruik. Voor verwarming en koeling kijk je naar **last**: wat moet het systeem kunnen dragen op een koude respectievelijk warme referentiedag (afhankelijk van de methode die je gebruikt).

## Waarom “één getal voor het hele huis” misleidend is

Kamers verschillen: zon, hoekkamer, veel glas, weinig isolatie. ComfortTool werkt **per ruimte** waar dat in de flow past, zodat je niet alleen een globaal plaatje ziet.

## Praktisch stappenplan

- Vul de scan zo eerlijk mogelijk in.
- Gebruik postcode voor klimaatcontext waar beschikbaar.
- Vergelijk scenario’s (bijv. betere isolatie of meer zonwering).

## Volgende stap

**Ga naar de scan** — je tussenresultaten blijven op je apparaat bewaard, zodat je later rustig verder kunt.`,
  },
  {
    slug: 'standaard-of-maatwerkrapport',
    title: 'Standaard of maatwerkrapport — wat heb ik nodig?',
    description:
      'Comfort Basis vs Plus, digitale levering, en wanneer je een vakman op locatie nodig hebt — in lijn met de ComfortTool webapp.',
    datePublished: '2026-01-22',
    keywords: 'Comfort Basis, Comfort Plus, rapport, maatwerk, digitale levering',
    body: `## Twee smaken in deze app

**Comfort Basis** geeft een vast opgebouwd rapport: duidelijk, voorspelbaar, zonder taalmodel.

**Comfort Plus** voegt een **uitgeschreven adviesrapport** toe (AI) dat je invoer en uitkomsten samenvat in leesbare taal — ideaal om te delen.

## Wanneer “maatwerk” echt maatwerk is

Voor complexe gebouwen, juridische trajecten of subsidie dossiers heb je vaak **meetwerk en een adviseur** nodig. ComfortTool is je **startpunt**, niet het hele dossier.

## Digitale levering

Na betaling werk je in je **account**: rapport genereren en (binnen limiet) opnieuw genereren. Geen fysieke levering.

## Volgende stap

Bekijk **Prijzen** en start daarna de **scan** — zo zie je wat bij jouw m² en segment past.`,
  },
  {
    slug: 'warmtepomp-kiezen-waar-begin-je',
    title: 'Een warmtepomp kiezen: waar begin je?',
    description:
      'Geen productverkoop — wél een helder stappenplan: lasten begrijpen, ruimte en geluid, en hoe je met ComfortTool richting geeft aan het gesprek met je installateur.',
    datePublished: '2026-02-01',
    keywords: 'warmtepomp, keuze, vermogen, installateur, comfort',
    body: `## Begin bij je vraag

Wil je vooral **lagere lasten**, **comfort in de zomer**, of **klaar voor de toekomst**? Je antwoord bepaalt welke vervolgstappen logisch zijn.

## Begrijp je huidige situatie

- Hoe is de **isolatie**?
- Hoe zit het met **ventilatie**?
- Zijn er **zonnige gevels** met veel glas?

ComfortTool helpt je **indicatieve warmte- en koellast** te zien — handig om niet op gevoel te kopen.

## Praat met vakmensen

Vraag altijd naar **SCOP/SEER**, **geluid**, **ruimte binnen**, en **hydraulisch inregelen**. ComfortTool vervangt dat gesprek niet — het maakt het **beter voorbereid**.

## Volgende stap

**Start de scan** en neem de uitkomst mee naar je installateur of adviseur.`,
  },
  {
    slug: 'uitleg-comforttool-rapport',
    title: 'Uitleg van je ComfortTool-rapport',
    description:
      'Wat je in het rapport terugziet, hoe je het moet lezen naast de cijfers, en waar je op moet letten bij Plus-teksten.',
    datePublished: '2026-02-08',
    keywords: 'ComfortTool rapport, uitleg, leesrapport, disclaimer',
    body: `## Cijfers eerst

Het hart blijft je **berekening**: kW, jaarschatting en per ruimte waar van toepassing. Dat is de harde basis.

## Tekst eromheen

Bij **Plus** voegt ComfortTool een **samenvatting** toe in begrijpelijke taal. Dat is bedoeld om te lezen en te delen — geen vervanging van een officiële berekening.

## Voorbehoud

Er staat altijd een **disclaimer**: indicatief, afhankelijk van invoer, geen energielabel. Gebruik het om keuzes voor te bereiden, niet om vergunningen of subsidies “zelf te verklaren”.

## Volgende stap

**Log in op je account**, open je aankoop en genereer je rapport — of kies je bundel via **Prijzen** als je nog geen aankoop hebt.`,
  },
  {
    slug: 'koellast-berekenen-airco-warmtepomp',
    title: 'Koellast berekenen: wat betekent het voor airco of warmtepomp?',
    description:
      'Hoe je indicatieve koellast koppelt aan apparaatkeuze, waarom kW niet hetzelfde is als “merk op de muur”, en hoe ComfortTool je gesprek met de installateur scherper maakt.',
    datePublished: '2026-02-15',
    keywords:
      'koellast berekenen, vermogen airco, warmtepomp koelen, kW koeling, installateur gesprek',
    body: `## Van last naar apparaat

**Koellast** is wat je ruimte of woning op een warme dag “vraagt” aan het systeem. Een **airco** of **warmtepomp** moet dat (met marge) aankunnen — maar het merk en het type bepalen ook hoe rustig, geluidarm en efficiënt dat gebeurt.

## Waarom alleen m² kijken niet genoeg is

Zon op het glas, ventilatie, interne warmte en isolatie veranderen de last per ruimte. Een globale vuistregel zonder die context maakt **te klein** of **te groot** kopen makkelijker dan je denkt.

## Hoe ComfortTool helpt

De **scan** geeft een **indicatief** beeld van koel- (en waar van toepassing warmte-)last. Neem die cijfers mee naar je installateur en laat ze **uitwerken** tot een offerte op jouw hydrauliek, binnenunit en regeling.

## Volgende stap

**Start de scan**, noteer de uitkomsten per ruimte en vraag expliciet naar **capaciteit bij jouw ontwerpbuien** en **SEER** — niet alleen naar het vermogen op het etiket.`,
  },
  {
    slug: 'zonwering-hr-glas-koellast',
    title: 'Zonwering en HR-glas: invloed op koellast en comfort',
    description:
      'Hoe zonwering en beter glas de zomerhitte dempen, wat dat doet met je indicatieve koellast, en waarom scenario’s vergelijken in ComfortTool zinvol is.',
    datePublished: '2026-02-22',
    keywords: 'koellast zonwering, triple glas koeling, HR glas zon, screens koel huis, comfort zomer',
    body: `## Zon op het raam = last in de kamer

Zonder bescherming tegen instraling wordt vooral **zuid- en westgevels** snel warm. **Zonwering** (buiten heeft vaak de meeste winst) en **beter glas** verlagen de warmte die binnenkomt — en daarmee ook je **koellast**.

## HR++, triple of voorzetglas

Elke verbetering telt, maar het effect hangt af van **oriëntatie**, **kozijn** en **bestaande situatie**. In de praktijk is het slim om niet alleen naar U-waarden te kijken, maar naar **comfort op hete uren**.

## Scenario in de app

Vul de **ComfortTool-scan** eerst “zoals nu” in en daarna een variant met **meer zonwering** of **beter glas** (voor zover je dat in de vragen kunt weergeven). Zo zie je **richting** van het verschil — geen vervanging van een detailberekening op locatie.

## Volgende stap

**Start de scan** en vergelijk twee scenario’s. Neem de uitkomst mee als je offertes voor beglazing of zonwering aanvraagt.`,
  },
  {
    slug: 'veelgemaakte-fouten-vermogen-inschatten',
    title: 'Veelgemaakte fouten bij inschatten van verwarmings- en koelvermogen',
    description:
      'Waarom oversizing en undersizing zo vaak voorkomen, welke aannames misleiden, en hoe je met een eerlijke scan-invoer en vakmanschap uit de valkuilen blijft.',
    datePublished: '2026-03-01',
    keywords: 'vermogen verwarming woning, oversizing airco, koelvermogen te klein, lastberekening fouten',
    body: `## Te groot “voor de zekerheid”

Meer kW dan nodig kost geld, kan **kortcyclen** geven en is niet automatisch comfortabeler. **Te klein** op piekdagen voelt meteen verkeerd. De sweet spot vraagt om **last** en **regeling** die bij elkaar passen.

## Alleen naar vloeroppervlak kijken

m² zonder **glas**, **zon** en **ventilatie** zegt weinig. Twee gelijke kamers op verschillende gevels kunnen een totaal andere **koellast** hebben.

## De oude ketel als maatstaf

“Vroeger stond er 28 kW dus dat moet nu ook” — vaak misleidend na **isolatie**, **warmtepomp** of **andere afgifte**. Begin bij de **actuele gebouw- en gebruiksgegevens**.

## Hoe ComfortTool past

Vul de **scan** zo **concreet** mogelijk in en gebruik het resultaat als **gespreksdocument** bij je installateur. Zij maken de definitieve dimensionering op normen, metingen en ervaring.

## Volgende stap

**Ga naar de scan** en controleer met name **ramen, zon en ventilatie** — daar zitten de grootste verschillen tussen woningen.`,
  },
  {
    slug: 'isde-verduurzamen-weten-voor-investering',
    title: 'ISDE en verduurzamen: wat je wilt weten vóór je investeert',
    description:
      'Hoe je subsidie en regelingen benadert zonder verrassingen: waar de officiële info staat, wat je zelf moet checken, en hoe ComfortTool naast (niet in plaats van) je dossier past.',
    datePublished: '2026-03-15',
    keywords: 'ISDE subsidie, warmtepomp subsidie, verduurzamen woning, RVO subsidie, investering 2026',
    body: `## Subsidie is regelwerk

De **Investeringssubsidie duurzame energie en energiebesparing (ISDE)** en aanverwante regelingen veranderen met **voorwaarden**, **bedragen** en **productlijsten**. Wat vorig jaar gold, is niet automatisch vandaag hetzelfde.

## Waar je de waarheid checkt

Lees en bevestig details altijd bij **RVO** en de **Rijksoverheid** (energie thuis). ComfortTool is **geen** subsidieadviseur en **geen** officiële aanvraagtool.

## Koppel techniek aan geld

Voor je tekent: welk **type** installatie, welke **combinatie** met isolatie of ventilatie, en of je product op de **actuele lijst** staat. Je installateur of een subsidie-specialist helpt vaak met de praktijkstappen.

## ComfortTool ernaast

Gebruik de **scan** om **indicatieve lasten** en scenario’s vast te leggen — handig bij keuzes én bij het voorbereiden van gesprekken. Het **rapport** vervangt geen subsidiedossier of energielabel.

## Volgende stap

**Start de scan** voor je cijfers, en plan parallel je check op **rvo.nl** voor actuele ISDE-informatie.`,
  },
  {
    slug: 'voorbereiden-op-zomer-koellast',
    title: 'Voorbereiden op de zomer: koellast en comfort nu checken',
    description:
      'Waarom voorjaar het moment is om koeling en zoninstraling te plannen, welke vragen je nu stelt aan vakmensen, en hoe je met ComfortTool voor de hittegolf klaar bent.',
    datePublished: '2026-03-29',
    keywords: 'koeling huis zomer, airco vermogen berekenen, hittegolf comfort, koellast voorjaar',
    body: `## Wacht niet tot de eerste 30 °C-dag

Als het druk wordt bij installateurs, heb je minder keuze in **planning** en **oplevering**. In het **voorjaar** tijd nemen voor **last**, **geluid** en **plek van binnenunits** loont.

## Koellast = je kompas

Weet **welk vermogen** ongeveer nodig is en welke **kamers** het zwaarst zijn. Dan kun je gericht vragen naar **meerdere binnenunits** versus één sterke kring, en naar **regeling per zone**.

## Zonwering en ventilatie eerst?

Soms verlagen **zonwering** of **betere nachtventilatie** de behoefte aan koelmachines. ComfortTool helpt om **scenario’s** naast elkaar te zien — indicatief, maar sterk als **discussiebasis**.

## Volgende stap

**Start de scan** en sla je scenario op. Vraag daarna offertes waarin de leverancier **capaciteit en werkpunten** toelicht bij jouw situatie.`,
  },
  {
    slug: 'verduurzamen-woning-2026-ontwikkelingen',
    title: 'Verduurzamen in 2026: waar let je op bij isolatie, ventilatie en warmtepomp?',
    description:
      'Een actueel overzicht in gewone taal: waarom regels en subsidies meebewegen, hoe je betrouwbare info vindt, en hoe je ComfortTool gebruikt om technische keuzes voor te bereiden.',
    datePublished: '2026-04-10',
    keywords:
      'verduurzamen woning 2026, isolatie ventilatie warmtepomp, energie regels woning, koellast verduurzaming',
    body: `## 2026: het jaar van de “even checken”

Rond **isolatie**, **ventilatie**, **warmtepomp** en **subsidies** verschuiven details vaak per jaar. Voor je investeert: even de **officiële bron** raadplegen — niet alleen een blog of forumbericht.

## Drie pijlers die samenhangen

**Isolatie** beïnvloedt je **verwarmingslast**. **Ventilatie** beïnvloedt gezondheid én soms temperatuur en vocht. **Warmtepomp of koeling** moet passen bij **afgifte**, **geluid** en **ruimte**. Als één pijler klopt en de andere niet, blijft comfort achter.

## Waarom ComfortTool hierbij past

Met de **scan** zet je **indicatieve warmte- en koellast** en jaarperspectief naast elkaar — ideaal om met installateur of adviseur dezelfde taal te spreken. Het is **geen** vervanging van maatwerk of wettelijke rapportages.

## Blijf up-to-date

Voor subsidies en voorwaarden: **RVO** en **Rijksoverheid**. Voor productkeuze: **technische fiches** en **erkende partijen** op locatie.

## Volgende stap

**Start de scan** met je huidige situatie, werk daarna je **officiële checks** af, en plan het vakgesprek met concrete vragen over last, geluid en planning.`,
  },
  {
    slug: 'isde-subsidieoverzicht-2026',
    title: 'ISDE subsidieoverzicht 2026: isolatie, glas, ventilatie en warmtepomp',
    description:
      'Actuele ISDE-bedragen voor 2026: wat er nieuw is bij isolatie, glasisolatie, ventilatiesystemen en lucht-waterwarmtepompen — en hoe ComfortTool je helpt de technische keuze voor te bereiden.',
    datePublished: '2026-05-01',
    keywords:
      'ISDE 2026, isolatie subsidie, warmtepomp subsidie, ventilatiesysteem subsidie, verduurzamen woning, RVO subsidie',
    body: `## Goed nieuws: ISDE loopt gewoon door in 2026

De **Investeringssubsidie duurzame energie en energiebesparing (ISDE)** blijft ook in 2026 actief. De meeste bedragen zijn gelijk gebleven ten opzichte van 2025, maar er zijn drie relevante wijzigingen:

- Nieuwe subsidie voor **ventilatiesystemen** (uitsluitend in combinatie met isolatie).
- Strengere eisen voor **warmtepompen** bij meerdere units.
- Versoepelde eisen voor **glasisolatie** bij monumenten.

Controleer altijd de actuele voorwaarden via **rvo.nl** — bedragen en productlijsten kunnen tussentijds wijzigen.

## Isolatie: vaste bedragen per m²

Voor de meeste isolatiemaatregelen geldt een **vast subsidiebedrag per vierkante meter**. Kies je voor **biobased materialen** (vlas, houtvezel, kurk), dan komt er een extra bonus bovenop.

| Maatregel | Subsidie per m² | Biobased bonus | Minimum | Maximum |
|---|---|---|---|---|
| Gevelisolatie | € 20,25 | + € 6,- | 10 m² | 170 m² |
| Spouwmuurisolatie | € 5,25 | + € 1,50 | 10 m² | 170 m² |
| Dakisolatie | € 16,25 | + € 5,- | 20 m² | 200 m² |
| Zoldervloerisolatie | € 4,- | + € 1,50 | 20 m² | 200 m² |
| Vloerisolatie | € 5,50 | + € 2,- | 20 m² | 130 m² |
| Bodemisolatie | € 3,- | + € 1,- | 20 m² | 130 m² |

De biobased bonus geldt voor materialen met een MKI-score van maximaal 1,90 bij een isolatiewaarde van 3,5 m²K/W. In de praktijk vallen dezelfde duurzame materialen als voorgaande jaren onder deze grens.

## Glasisolatie: HR++ en triple glas

De subsidie voor glasisolatie is ongewijzigd. Minimale oppervlakte is **3 m²**, maximum **45 m²**.

- **HR++ glas**: € 25,- per m²
- **Triple glas**: € 111,- per m² (kozijnen moeten mee vervangen worden)
- **HR++ voor monumenten**: € 46,- per m² — dit jaar versoepeld: maximale isolatiewaarde nu 5,8 W/m²K (vorig jaar: 3,0 W/m²K)

Voor triple glas geldt geen maximale isolatiewaarde meer voor kozijnen: alle moderne kozijnen voldoen.

## Ventilatiesysteem: nieuw in 2026

Vanaf 2026 valt een **energiezuinig ventilatiesysteem** ook onder de ISDE. Voorwaarden:

- Je vraagt de ventilatie-subsidie **altijd in combinatie** met ten minste één isolatiemaatregel.
- Alleen energiezuinige systemen komen in aanmerking, zoals **CO2-gestuurde ventilatie** of een **warmteterugwinning-unit (WTW)**.
- Het subsidiebedrag is een **eenmalig vast bedrag van € 400,-**.

## Lucht-waterwarmtepomp: aangescherpte regels

De berekeningswijze is veranderd. Voorheen ontving je bij meerdere units telkens opnieuw een startbedrag. Dat is afgelopen:

| Onderdeel | Bedrag | Toekenning |
|---|---|---|
| Startbedrag | € 1.025,- | Eenmalig (eerste unit) |
| Energielabelbonus | € 200,- | Eenmalig (eerste unit) |
| Subsidie per kW | € 225,- | Per kW voor alle units |

**Formule**: € 1.025 + € 200 + (€ 225 x vermogen in kW)

Daarnaast worden **split lucht-waterwarmtepompen met GWP > 750 en een koudemiddelgewicht onder de 3 kg** per 2026 uitgesloten vanwege milieubelasting. Vraag je installateur of het door jou gekozen model nog op de actuele ISDE-productlijst staat.

## Wat betekent dit voor je technische keuze?

Subsidie volgt de techniek — niet andersom. Het loont om eerst de **indicatieve warmtelast** van je woning te kennen:

- Hoeveel kW warmtepomp heb je echt nodig?
- Is je woning al voldoende geïsoleerd om een warmtepomp efficiënt te laten draaien?
- Welk ventilatiesysteem past bij de bouwkundige staat?

De **ComfortTool-scan** geeft je een indicatief beeld van warmte- en koellast per ruimte — een handig vertrekpunt voor het gesprek met je installateur, voordat je een offerte tekent.

## Volgende stap

**Start de scan** voor een indicatief beeld van jouw situatie. Controleer daarna de actuele ISDE-voorwaarden op **rvo.nl** en laat een erkend vakspecialist de officiële aanvraag verzorgen — dat is een vereiste voor de subsidie.`,
  },
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
